import { useCallback } from 'react';
import { clsx } from 'clsx';
import { motion } from 'framer-motion';
import { useFilters } from '@/context/FiltersContext';
import { FUEL_TYPES } from '@/types';
import { FUEL_TYPE_LABELS, FUEL_TYPE_COLORS } from '@/constants';
import type { FuelType } from '@/types';

interface FuelTypeFilterProps {
  className?: string;
}

export function FuelTypeFilter({ className }: FuelTypeFilterProps) {
  const { selectedFuelTypes, primaryFuelType, toggleFuelType, setPrimaryFuelType } = useFilters();

  const handleClick = useCallback(
    (fuelType: FuelType) => {
      const isSelected = selectedFuelTypes.includes(fuelType);

      if (isSelected) {
        // If it's already selected and is the primary, just keep it as is
        // (clicking the primary doesn't deselect it if it's the only one)
        if (fuelType === primaryFuelType) {
          // If there's only one selected, don't allow deselecting
          if (selectedFuelTypes.length === 1) {
            return;
          }
          // If clicking the primary, deselect it and set new primary
          toggleFuelType(fuelType);
        } else {
          // Clicking a selected non-primary makes it primary
          setPrimaryFuelType(fuelType);
        }
      } else {
        // Selecting a new fuel type makes it primary
        toggleFuelType(fuelType);
        setPrimaryFuelType(fuelType);
      }
    },
    [selectedFuelTypes, primaryFuelType, toggleFuelType, setPrimaryFuelType]
  );

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">Fuel Types</h3>
        <span className="text-xs text-gray-500">Tap to set primary</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {FUEL_TYPES.map((fuelType) => {
          const isSelected = selectedFuelTypes.includes(fuelType);
          const isPrimary = primaryFuelType === fuelType;
          const color = FUEL_TYPE_COLORS[fuelType];

          return (
            <motion.button
              key={fuelType}
              type="button"
              onClick={() => handleClick(fuelType)}
              whileTap={{ scale: 0.95 }}
              className={clsx(
                'relative px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                'border-2 focus:outline-none focus:ring-2 focus:ring-offset-1',
                isSelected
                  ? 'text-white'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              )}
              style={
                isSelected
                  ? {
                      backgroundColor: color,
                      borderColor: color,
                    }
                  : undefined
              }
              aria-pressed={isSelected}
              aria-label={`${FUEL_TYPE_LABELS[fuelType]}${isPrimary ? ' (primary, shown on map)' : ''}`}
            >
              {/* Primary indicator */}
              {isPrimary && (
                <motion.span
                  layoutId="primary-indicator"
                  className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full border-2 flex items-center justify-center"
                  style={{ borderColor: color }}
                >
                  <svg className="w-2.5 h-2.5" style={{ color }} fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </motion.span>
              )}

              {/* Colour dot for unselected */}
              {!isSelected && (
                <span
                  className="inline-block w-2 h-2 rounded-full mr-1.5"
                  style={{ backgroundColor: color }}
                />
              )}

              {FUEL_TYPE_LABELS[fuelType]}
            </motion.button>
          );
        })}
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Primary fuel type is shown on map markers. Tap a selected fuel type to make it primary.
      </p>
    </div>
  );
}
